import { InjectionToken } from '@angular/core';

/**
 * Configuration interface for the V2 logging system.
 * Apps provide this configuration to customize logging behavior.
 */
export interface LogConfig {
  /**
   * Application source identifier for logs.
   * Examples: 'payment-app', 'admin-app', 'link-generator', 'simulator'
   */
  source: string;

  /**
   * Enable V2 logging system with batch submission.
   * When false, falls back to V1 logging behavior.
   */
  enableV2Logging: boolean;

  /**
   * Maximum number of log entries to buffer before automatic flush.
   * Default: 25
   */
  bufferSize?: number;

  /**
   * Interval in milliseconds between automatic buffer flushes.
   * Default: 5000 (5 seconds)
   */
  flushIntervalMs?: number;
}

/**
 * Default logging configuration used when no app-specific config is provided.
 */
export const DEFAULT_LOG_CONFIG: LogConfig = {
  source: 'unknown-app',
  enableV2Logging: false,
  bufferSize: 25,
  flushIntervalMs: 5000,
};

/**
 * Injection token for logging configuration.
 * Apps should provide this token in their app.config.ts:
 *
 * @example
 * ```typescript
 * providers: [
 *   {
 *     provide: LOG_CONFIG,
 *     useValue: {
 *       source: 'payment-app',
 *       enableV2Logging: environment.production,
 *       bufferSize: 25,
 *       flushIntervalMs: 5000
 *     }
 *   }
 * ]
 * ```
 */
export const LOG_CONFIG = new InjectionToken<LogConfig>('LOG_CONFIG', {
  providedIn: 'root',
  factory: () => DEFAULT_LOG_CONFIG,
});
